/**
 * Coordinación de sesión: clasificación de errores auth, gracia de contexto y logout unificado.
 */

export const GRACE_PERIOD_MS = 5000;
const GRACE_STORAGE_KEY = "auth:context-grace-until";
const CONTEXT_SWITCH_UNTIL_KEY = "auth:context-switch-until";

export type SessionLogoutReason =
  | "token_expired"
  | "token_invalid"
  | "user_inactive"
  | "missing_tenant_context"
  | "unknown";

export type AuthFailureAction =
  "logout" | "no_logout" | "redirect_subscription";

export interface AuthFailureClassification {
  reason: SessionLogoutReason | "no_logout";
  action: AuthFailureAction;
}

const TOKEN_INVALID_PATTERNS = [
  /token inv[aá]lido/i,
  /token expirado/i,
  /invalid token/i,
  /expired/i,
  /jwt expired/i,
  /usuario inactivo/i,
  /usuario no encontrado/i,
  /formato de token/i,
  /authorization required/i,
  /token de autorizaci[oó]n requerido/i,
];

const TENANT_CONTEXT_PATTERNS = [
  /x-subscription-id/i,
  /header x-subscription-id/i,
  /suscripci[oó]n requerida/i,
  /contexto de tenant/i,
  /contexto tenant/i,
];

function readStorageNumber(key: string): number | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeStorageNumber(key: string, value: number): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(key, String(value));
  } catch {
    // ignore quota / private mode
  }
}

function removeStorageKey(key: string): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

let companySelectionTimestamp: number | null = null;
let isRedirecting = false;
let contextSwitchDepth = 0;

export function apiDebugLog(
  message: string,
  data?: Record<string, unknown>,
): void {
  if (import.meta.env.VITE_API_DEBUG !== "true") return;
  if (data) {
    console.log(`[API] ${message}`, data);
  } else {
    console.log(`[API] ${message}`);
  }
}

export function getIsRedirecting(): boolean {
  return isRedirecting;
}

export function resetRedirectingFlag(): void {
  isRedirecting = false;
}

function persistGraceUntil(until: number): void {
  writeStorageNumber(GRACE_STORAGE_KEY, until);
}

function readPersistedGraceUntil(): number | null {
  return readStorageNumber(GRACE_STORAGE_KEY);
}

export function markCompanySelected(): void {
  const until = Date.now() + GRACE_PERIOD_MS;
  companySelectionTimestamp = Date.now();
  persistGraceUntil(until);
  apiDebugLog("markCompanySelected", { gracePeriodMs: GRACE_PERIOD_MS });
}

export function markSubscriptionSelected(): void {
  const until = Date.now() + GRACE_PERIOD_MS;
  companySelectionTimestamp = Date.now();
  persistGraceUntil(until);
  apiDebugLog("markSubscriptionSelected", { gracePeriodMs: GRACE_PERIOD_MS });
}

export function isInGracePeriod(): boolean {
  const persistedUntil = readPersistedGraceUntil();
  if (persistedUntil !== null && Date.now() < persistedUntil) {
    return true;
  }
  if (companySelectionTimestamp === null) {
    return false;
  }
  return Date.now() - companySelectionTimestamp < GRACE_PERIOD_MS;
}

export function beginContextSwitch(durationMs = GRACE_PERIOD_MS): void {
  contextSwitchDepth += 1;
  const until = Date.now() + durationMs;
  writeStorageNumber(CONTEXT_SWITCH_UNTIL_KEY, until);
  persistGraceUntil(until);
  companySelectionTimestamp = Date.now();
}

export function endContextSwitch(): void {
  contextSwitchDepth = Math.max(0, contextSwitchDepth - 1);
  if (contextSwitchDepth === 0) {
    removeStorageKey(CONTEXT_SWITCH_UNTIL_KEY);
  }
}

export function isContextSettling(): boolean {
  if (contextSwitchDepth > 0) return true;
  const until = readStorageNumber(CONTEXT_SWITCH_UNTIL_KEY);
  return until !== null && Date.now() < until;
}

function extractReasonCode(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const record = payload as Record<string, unknown>;
  return typeof record.reasonCode === "string" ? record.reasonCode : undefined;
}

function extractMessage(payload: unknown, fallback = ""): string {
  if (!payload || typeof payload !== "object") return fallback;
  const record = payload as Record<string, unknown>;
  const raw = record.message;
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw)) {
    return raw
      .filter((item): item is string => typeof item === "string")
      .join(". ");
  }
  if (raw && typeof raw === "object") {
    const nested = raw as Record<string, unknown>;
    if (typeof nested.message === "string") return nested.message;
  }
  if (typeof record.error === "string") return record.error;
  return fallback;
}

export function classifyAuthFailure(
  status: number,
  payload: unknown,
  pathOrUrl = "",
): AuthFailureClassification {
  const reasonCode = extractReasonCode(payload);
  const message = extractMessage(payload);

  if (status === 403) {
    if (reasonCode === "MISSING_PERMISSION") {
      return { reason: "no_logout", action: "no_logout" };
    }
    if (reasonCode === "MISSING_CONTEXT") {
      const missing = extractMissingContext(payload);
      if (missing.includes("x-subscription-id")) {
        return {
          reason: "missing_tenant_context",
          action: "redirect_subscription",
        };
      }
      return { reason: "no_logout", action: "no_logout" };
    }
    return { reason: "no_logout", action: "no_logout" };
  }

  if (status !== 401) {
    return { reason: "no_logout", action: "no_logout" };
  }

  if (reasonCode === "MISSING_CONTEXT") {
    return {
      reason: "missing_tenant_context",
      action: "redirect_subscription",
    };
  }

  const haystack = `${message} ${pathOrUrl}`;
  if (TENANT_CONTEXT_PATTERNS.some((pattern) => pattern.test(haystack))) {
    return {
      reason: "missing_tenant_context",
      action: "redirect_subscription",
    };
  }

  if (/usuario inactivo/i.test(message)) {
    return { reason: "user_inactive", action: "logout" };
  }

  if (TOKEN_INVALID_PATTERNS.some((pattern) => pattern.test(message))) {
    if (/expirado|expired/i.test(message)) {
      return { reason: "token_expired", action: "logout" };
    }
    return { reason: "token_invalid", action: "logout" };
  }

  // 401 genérico con token presente durante cambio de contexto → no logout
  if (isInGracePeriod() || isContextSettling()) {
    return { reason: "no_logout", action: "no_logout" };
  }

  return { reason: "unknown", action: "logout" };
}

function extractMissingContext(payload: unknown): string[] {
  if (!payload || typeof payload !== "object") return [];
  const record = payload as Record<string, unknown>;
  const raw = record.missingContext;
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is string => typeof item === "string");
}

export function shouldLogoutFromClassification(
  classification: AuthFailureClassification,
  hasToken: boolean,
  status: number,
): boolean {
  if (classification.action === "logout") return true;
  if (classification.action === "redirect_subscription") return false;
  if (classification.action === "no_logout") return false;
  if (status === 403 && hasToken) return false;
  return false;
}

export interface LogoutSessionOptions {
  preserveTheme?: boolean;
  redirectTo?: string;
}

const PRESERVED_KEYS = new Set([
  "unifik-theme",
  "data-table:page-size",
  "browser-compat:dismissed",
]);
const PRESERVED_PREFIXES = ["data-table:layouts:", "data-table:active:"];

/**
 * Limpia sesión y redirige a login. Idempotente si ya hay redirección en curso.
 */
export function logoutSession(options: LogoutSessionOptions = {}): void {
  if (typeof window === "undefined") return;
  if (isRedirecting) return;

  const redirectTo = options.redirectTo ?? "/login";
  const currentPath = window.location.pathname;
  if (currentPath.includes("/login") && redirectTo === "/login") {
    return;
  }

  isRedirecting = true;

  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key === null) continue;
      if (PRESERVED_KEYS.has(key)) continue;
      if (PRESERVED_PREFIXES.some((prefix) => key.startsWith(prefix))) continue;
      keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    sessionStorage.clear();
  } catch (error) {
    apiDebugLog("logoutSession cleanup error", {
      error: error instanceof Error ? error.message : String(error),
    });
  }

  if (!window.location.pathname.includes(redirectTo)) {
    window.location.href = redirectTo;
  } else {
    isRedirecting = false;
  }
}

export function redirectToSubscriptionSelection(): void {
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  if (path.includes("/select-subscription") || path.includes("/login")) {
    return;
  }
  window.location.href = "/select-subscription";
}

export interface HandleAuthErrorInput {
  status: number;
  pathOrUrl: string;
  payload?: unknown;
  hasToken: boolean;
}

/**
 * Decide y ejecuta acción ante error auth HTTP. Retorna true si se manejó (sin throw adicional).
 */
export function handleAuthErrorResponse(input: HandleAuthErrorInput): boolean {
  const { status, pathOrUrl, payload, hasToken } = input;

  if (status === 429) return true;

  const publicRoutes = [
    "/auth/login",
    "/auth/forgot-password",
    "/auth/reset-password",
    "/auth/refresh",
    "/auth/register",
    "/dispatch-guides/public/",
  ];
  const exemptRoutes = ["/health", "/status"];

  if (publicRoutes.some((route) => pathOrUrl.includes(route))) return true;
  if (exemptRoutes.some((route) => pathOrUrl.includes(route))) return true;

  if (typeof window !== "undefined") {
    const currentPath = window.location.pathname;
    if (
      currentPath.includes("/login") ||
      currentPath.includes("/forgot-password") ||
      currentPath.includes("/reset-password")
    ) {
      return true;
    }
  }

  if (status !== 401 && status !== 403) return false;

  if (isInGracePeriod() || isContextSettling()) {
    apiDebugLog("handleAuthError skipped — grace or context switch", {
      status,
      pathOrUrl,
    });
    return true;
  }

  const classification = classifyAuthFailure(status, payload, pathOrUrl);
  const reasonCode = extractReasonCode(payload);
  const message = extractMessage(payload);

  apiDebugLog("handleAuthError classified", {
    status,
    pathOrUrl,
    reasonCode,
    message,
    hasToken,
    classification,
    subscriptionId: localStorage.getItem("selectedSubscriptionId"),
    companyId: localStorage.getItem("selectedCompanyId"),
  });

  if (classification.action === "redirect_subscription") {
    redirectToSubscriptionSelection();
    return true;
  }

  if (classification.action === "no_logout") {
    return true;
  }

  const shouldLogout = shouldLogoutFromClassification(
    classification,
    hasToken,
    status,
  );

  if (!shouldLogout) {
    return true;
  }

  if (!hasToken && status === 403) {
    logoutSession();
    return true;
  }

  if (status === 401 || (status === 403 && !hasToken)) {
    logoutSession();
    return true;
  }

  return true;
}

/** Mutex para refresh concurrente */
let refreshInFlight: Promise<string | null> | null = null;

export async function refreshTokenOnce(
  refreshFn: () => Promise<{ token: string }>,
): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const result = await refreshFn();
      return result.token ?? null;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}
